"use client";

import { useEffect, useMemo, useRef } from "react";
import { AnimatePresence } from "motion/react";
import {
  useAnalyzeFileContext,
  type InsightMessage,
  type Persona,
} from "../context/AnalyzeFileContext";
import { ANALYZE_SELECTION_CLASSES } from "../analyzeConstants";
import { cn } from "@/modern/utils";
import { useFeedStickyPersona } from "../hooks/useFeedStickyPersona";
import { AnalyzeEmptyFeedState } from "./AnalyzeEmptyFeedState";
import { AnalyzeFeedStickyHeader } from "./AnalyzeFeedStickyHeader";
import { AnalyzeGeneratingIndicator } from "./AnalyzeGeneratingIndicator";
import { AnalyzeInsightCard } from "./AnalyzeInsightCard";

type AnalyzeInsightsFeedProps = {
  messages: InsightMessage[];
  feedFilter: string | null;
  onDeleteMessage: (id: string) => void;
  /** Docked in File Detail rail — shell already provides padding. */
  flushHorizontal?: boolean;
};

export const AnalyzeInsightsFeed = ({
  messages,
  feedFilter,
  onDeleteMessage,
  flushHorizontal = false,
}: AnalyzeInsightsFeedProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingAnchorRef = useRef<HTMLDivElement>(null);
  const { personas, typingPersonas, generatingUiModes, getGenerationStartedAt } =
    useAnalyzeFileContext();
  const prevCountRef = useRef(messages.length);

  const filteredMessages = useMemo(() => {
    const sorted = [...messages].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    if (!feedFilter) return sorted;
    return sorted.filter((m) => m.personaId === feedFilter);
  }, [messages, feedFilter]);

  const insightCountByPersona = useMemo(() => {
    const counts = new Map<string, number>();
    const source = feedFilter ? filteredMessages : messages;
    source.forEach((m) => {
      counts.set(m.personaId, (counts.get(m.personaId) ?? 0) + 1);
    });
    return counts;
  }, [messages, filteredMessages, feedFilter]);

  const stickyPersonaId = useFeedStickyPersona(scrollRef, filteredMessages, feedFilter);

  const stickyPersona = useMemo(
    () => (stickyPersonaId ? personas.find((p) => p.id === stickyPersonaId) ?? null : null),
    [stickyPersonaId, personas],
  );

  const focusedPersona = useMemo(
    () => (feedFilter ? personas.find((p) => p.id === feedFilter) ?? null : null),
    [feedFilter, personas],
  );

  const generatingPersonas = useMemo(() => {
    const ids = Array.from(typingPersonas);
    if (feedFilter && !ids.includes(feedFilter)) {
      return ids
        .map((id) => personas.find((p) => p.id === id))
        .filter((p): p is Persona => !!p)
        .filter((p) => p.id === feedFilter);
    }
    return ids
      .map((id) => personas.find((p) => p.id === id))
      .filter((p): p is Persona => !!p)
      .filter((p) => !feedFilter || p.id === feedFilter);
  }, [typingPersonas, personas, feedFilter]);

  const isGeneratingForView = generatingPersonas.length > 0;
  const displayStickyPersona = stickyPersona ?? generatingPersonas[0] ?? null;
  const showStickyHeader =
    !!displayStickyPersona && (filteredMessages.length > 0 || isGeneratingForView);

  useEffect(() => {
    const grew = messages.length > prevCountRef.current;
    prevCountRef.current = messages.length;
    if (generatingPersonas.length > 0) {
      typingAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } else if (grew) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, generatingPersonas.length, typingPersonas.size]);

  return (
    <div
      ref={scrollRef}
      className={cn(
        "flex-1 min-h-0 overflow-y-auto pb-2",
        flushHorizontal ? "px-0" : "px-3",
        showStickyHeader ? "pt-0" : flushHorizontal ? "pt-2" : "pt-3",
        ANALYZE_SELECTION_CLASSES,
      )}
    >
      {showStickyHeader && displayStickyPersona && (
        <AnalyzeFeedStickyHeader
          persona={displayStickyPersona}
          insightCount={insightCountByPersona.get(displayStickyPersona.id) ?? 0}
          isGenerating={typingPersonas.has(displayStickyPersona.id)}
          isScrollLinked={!feedFilter}
        />
      )}

      <div className="flex flex-col gap-2.5">
        {filteredMessages.map((message) => {
          const persona = personas.find((p) => p.id === message.personaId);
          if (!persona) return null;
          const hideAccentBar =
            showStickyHeader && displayStickyPersona?.id === persona.id;
          return (
            <AnalyzeInsightCard
              key={message.id}
              message={message}
              persona={persona}
              onDelete={onDeleteMessage}
              hideAccentBar={hideAccentBar}
            />
          );
        })}

        <div ref={typingAnchorRef}>
          <AnimatePresence initial={false}>
            {generatingPersonas.map((persona) => (
              <AnalyzeGeneratingIndicator
                key={`typing-${persona.id}`}
                persona={persona}
                mode={generatingUiModes[persona.id] ?? "compact"}
                getGenerationStartedAt={getGenerationStartedAt}
                hidePersonaHeader={showStickyHeader}
              />
            ))}
          </AnimatePresence>
        </div>

        {filteredMessages.length === 0 && !isGeneratingForView && (
          <AnalyzeEmptyFeedState
            focusedPersona={focusedPersona}
            isGenerating={false}
            hasFilter={!!feedFilter}
          />
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};
